import { api } from './axios'
import type { User } from '../models'

interface LoginResponse {
  token: string
  user: User
}

interface RegisterRequest {
  name: string
  email: string
  password: string
  password_confirmation: string
  funcao_usuario: string
  setor_usuario_fk: number
  cargo_usuario?: string
}

class AuthService {
  private readonly TOKEN_KEY = 'token'
  private readonly USER_KEY = 'user'
  private userCache: User | null = null
  private userPromise: Promise<User> | null = null

  /**
   * Fazer login com email e senha
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      const response = await api.post<LoginResponse>('/login', {
        email,
        password,
      })

      const { token, user } = response.data

      // Salvar dados no localStorage
      this.saveAuth(token, user)

      return { token, user }
    } catch (error) {
      console.error('Erro no login:', error)
      throw error
    }
  }

  /**
   * Registrar novo usuário
   */
  async register(data: RegisterRequest): Promise<LoginResponse> {
    try {
      const response = await api.post<LoginResponse>('/register', data)

      const { token, user } = response.data

      // Salvar dados no localStorage
      this.saveAuth(token, user)

      return { token, user }
    } catch (error) {
      console.error('Erro no registro:', error)
      throw error
    }
  }

  /**
   * Fazer logout
   */
  async logout(): Promise<void> {
    try {
      // Tentar fazer logout no servidor (deletar token)
      await api.post('/logout')
    } catch (error) {
      console.error('Erro no logout do servidor:', error)
      // Continuar com logout local mesmo se der erro no servidor
    } finally {
      // Sempre limpar dados locais
      this.clearAuth()
    }
  }

  /**
   * Obter usuário atual autenticado (com cache para evitar requests desnecessários)
   */
  async getCurrentUser(forceRefresh = false): Promise<User> {
    try {
      if (forceRefresh) {
        this.resetUserCache()
      }

      // Se já tem cache válido, retornar
      if (this.userCache) {
        return this.userCache
      }

      // Se já tem uma promise em andamento, aguardar ela
      if (this.userPromise) {
        return this.userPromise
      }

      // Criar nova promise
      this.userPromise = this._fetchUser()
      const user = await this.userPromise

      // Limpar promise e cachear resultado
      this.userPromise = null
      this.userCache = user

      return user
    } catch (error) {
      this.userPromise = null
      this.userCache = null
      console.error('Erro ao obter usuário atual:', error)
      throw error
    }
  }

  /**
   * Buscar usuário da API (método privado)
   */
  private async _fetchUser(): Promise<User> {
    const response = await api.get<User>('/user')
    return response.data
  }

  /**
   * Verificar se está autenticado
   */
  isAuthenticated(): boolean {
    const token = this.getToken()
    return !!token
  }

  /**
   * Obter token do localStorage
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY)
  }

  /**
   * Definir token diretamente (usado no callback SSO)
   */
  setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token)
  }

  /**
   * Obter usuário do localStorage
   */
  getUser(): User | null {
    const userStr = localStorage.getItem(this.USER_KEY)
    if (!userStr) return null

    try {
      return JSON.parse(userStr)
    } catch {
      return null
    }
  }

  /**
   * Salvar dados de autenticação
   */
  saveAuth(token: string, user: User): void {
    localStorage.setItem(this.TOKEN_KEY, token)
    localStorage.setItem(this.USER_KEY, JSON.stringify(user))
    this.userCache = user // Atualizar cache
    
    // Garantir que o tema seja light mode após login (se não houver preferência salva)
    this.ensureDefaultTheme()
  }

  /**
   * Garantir tema padrão (light mode) se não houver preferência salva
   */
  private ensureDefaultTheme(): void {
    const themePreference = localStorage.getItem('theme-preference')
    
    // Se não houver preferência salva, definir como light mode
    if (!themePreference) {
      localStorage.setItem('theme-preference', 'light')
      document.documentElement.classList.remove('dark')
    }
  }

  /**
   * Atualizar apenas o usuário no storage e cache
   */
  saveUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user))
    this.userCache = user
  }

  /**
   * Limpar dados de autenticação
   */
  clearAuth(): void {
    localStorage.removeItem(this.TOKEN_KEY)
    localStorage.removeItem(this.USER_KEY)
    localStorage.removeItem('auth-storage') // Limpar Zustand storage também
    localStorage.removeItem('theme-preference') // Resetar tema ao deslogar
    this.resetUserCache() // Limpar cache
    
    // Garantir que volte ao light mode ao deslogar
    document.documentElement.classList.remove('dark')
  }

  private resetUserCache(): void {
    this.userCache = null
    this.userPromise = null
  }

  /**
   * Verificar e renovar autenticação (para compatibilidade com código existente)
   * Como estamos usando tokens Bearer, não precisamos do Sanctum CSRF
   */
  async ensureSanctumInitialized(): Promise<void> {
    // Com tokens Bearer, não precisamos inicializar CSRF
    // Esta função existe apenas para compatibilidade com o código existente

    // Verificar se o token ainda é válido
    const token = this.getToken()
    if (!token) {
      throw new Error('Token de autenticação não encontrado')
    }

    // Opcionalmente, verificar se o token ainda é válido fazendo uma requisição simples
    try {
      await this.getCurrentUser()
    } catch (error: any) {
      // Se der erro 401, limpar autenticação
      if (error.response?.status === 401) {
        this.clearAuth()
        throw new Error('Token de autenticação expirado')
      }
      throw error
    }
  }

  /**
   * Processar retorno do SSO Microsoft
   * Esta função deve ser chamada quando o usuário retorna do login Microsoft
   */
  async processSSOCallback(urlParams: URLSearchParams): Promise<User | null> {
    try {
      // Se há erro na URL, tratar
      const error = urlParams.get('error')
      if (error) {
        console.error('Erro SSO:', error)

        if (error === 'sso_not_linked') {
          throw new Error(
            'Usuário não encontrado no sistema. Verifique se seu email está cadastrado.'
          )
        }

        throw new Error(`Erro na autenticação: ${error}`)
      }

      // Se há token na URL, usar ele
      const token = urlParams.get('token')
      if (token) {
        // Buscar dados do usuário com o token
        localStorage.setItem(this.TOKEN_KEY, token)

        try {
          const user = await this.getCurrentUser()
          this.saveAuth(token, user)
          return user
        } catch (error) {
          this.clearAuth()
          throw error
        }
      }

      return null
    } catch (error) {
      console.error('Erro ao processar callback SSO:', error)
      throw error
    }
  }

  /**
   * Iniciar login SSO Microsoft
   * Usa configuração automática de ambiente
   */
  startMicrosoftSSO(): void {
    // Importação dinâmica para evitar dependência circular
    import('../config/environment').then(({ getSsoUrl }) => {
      window.location.href = getSsoUrl();
    });
  }
}

// Instância única do serviço
export const authService = new AuthService()
export default authService
